import React, {useState} from "react"
import {Link, useLocation} from "react-router-dom";
import styled from "styled-components";
import {Center} from "../shared/mix";

interface Props {
    children: React.ReactNode
}

// Trailing-slash paths match the URLs the static host serves (/sluzby/ -> sluzby/index.html),
// so prerendered links don't bounce through a redirect for crawlers.
const navLinks = [
    {to: "/", label: "Home"},
    {to: "/sluzby/", label: "Služby"},
    {to: "/reference/", label: "Reference"},
]

const normalizePath = (path: string) => path.replace(/\/+$/, "") || "/"

const Layout = (props: Props) => {
    const [menuOpen, setMenuOpen] = useState(false)
    const location = useLocation()
    const currentPath = normalizePath(location.pathname)

    return (
        <Website>
            <Nav menuOpen={menuOpen}>
                <NavInner>
                    <Link to="/" style={{textDecoration: 'none', color: 'inherit'}}>
                        <Logo>Matěj Půhoný</Logo>
                    </Link>
                    <NavLinks menuOpen={menuOpen}>
                        {navLinks.map(link => (
                            <NavItem key={link.to} active={currentPath === normalizePath(link.to)}>
                                <Link to={link.to} onClick={() => setMenuOpen(false)}>
                                    {link.label}
                                </Link>
                            </NavItem>
                        ))}
                        <NavCta>
                            <Link to="/kontakt/" onClick={() => setMenuOpen(false)}>
                                Kontakt
                            </Link>
                        </NavCta>
                    </NavLinks>
                    <Burger onClick={() => setMenuOpen(!menuOpen)}>
                        {menuOpen ? (
                            <i className="fa-solid fa-xmark" />
                        ) : (
                            <i className="fa-solid fa-bars" />
                        )}
                    </Burger>
                </NavInner>
            </Nav>
            <Main>
                {props.children}
            </Main>
            <Center>
                <Footer>
                    <FooterLeft>
                        designed & coded by <Name>Matěj Půhoný</Name>
                    </FooterLeft>
                    <FooterRight>
                        {new Date().getFullYear()} | <a href="http://codeartists.eu/" target="_blank" rel="noopener noreferrer">#CodeArtists</a>
                    </FooterRight>
                </Footer>
            </Center>
        </Website>
    )
}

export default Layout

const Website = styled.div`
  box-sizing: border-box;
`

interface NavProps {
    menuOpen: boolean
}

const Nav = styled.nav<NavProps>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  z-index: 1000;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);

  @media (max-width: 680px) {
    background: ${({menuOpen}) => menuOpen ? "rgba(255, 255, 255, 1)" : "rgba(255, 255, 255, 0.85)"};
    backdrop-filter: ${({menuOpen}) => menuOpen ? "blur(0)" : "blur(12px)"};
  }
`

const NavInner = styled.div`
  max-width: 1300px;
  margin: 0 auto;
  padding: 0 2em;
  height: 4em;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const Main = styled.main`
  min-height: calc(100vh - 22em);
`

/* ---- logo ---- */

const Logo = styled.div`
  font-size: 1.4em;
  font-weight: 700;
  letter-spacing: 0.02em;
  cursor: pointer;
`

/* ---- nav links ---- */

const NavLinks = styled.div<NavProps>`
  display: flex;
  align-items: center;
  gap: 0.2em;

  @media (max-width: 680px) {
    display: ${({menuOpen}) => menuOpen ? "flex" : "none"};
    position: fixed;
    top: 4em;
    left: 0;
    width: 100%;
    box-sizing: border-box;
    background: #fff;
    border-bottom: 1px solid #ebebeb;
    padding: 1.2em 2em 1.5em;
    flex-direction: column;
    align-items: stretch;
    gap: 0.3em;
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.05);
  }
`

interface NavItemProps {
    active: boolean
}

const NavItem = styled.div<NavItemProps>`
  a {
    display: block;
    padding: 0.45em 0.9em;
    color: inherit;
    text-decoration: none;
    font-size: 0.85em;
    font-weight: 500;
    opacity: ${({active}) => active ? 1 : 0.45};
    border-radius: 0.3em;
    transition: opacity 0.2s ease, background 0.2s ease;
    letter-spacing: 0.01em;

    &:hover {
      opacity: 0.85;
      background: rgba(0, 0, 0, 0.03);
    }
  }

  @media (max-width: 680px) {
    a {
      padding: 0.7em 0.8em;
      font-size: 0.95em;
    }
  }
`

const NavCta = styled.div`
  margin-left: 0.5em;

  a {
    display: block;
    padding: 0.45em 1em;
    background: rgba(25, 25, 25, 1);
    color: #fff;
    text-decoration: none;
    font-size: 0.82em;
    font-weight: 600;
    border-radius: 0.35em;
    letter-spacing: 0.01em;
    transition: background 0.2s ease, transform 0.2s ease;

    &:hover {
      background: rgba(25, 25, 25, 0.82);
      transform: translateY(-1px);
    }
  }

  @media (max-width: 680px) {
    margin-left: 0;
    margin-top: 0.5em;

    a {
      text-align: center;
      padding: 0.7em 1em;
      font-size: 0.95em;
    }
  }
`

const Burger = styled.div`
  font-size: 1.15em;
  cursor: pointer;
  width: 2em;
  height: 2em;
  display: none;
  align-items: center;
  justify-content: center;
  border-radius: 0.3em;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
  }

  @media (max-width: 680px) {
    display: flex;
  }
`

/* ---- footer ---- */

const Footer = styled.footer`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid #eeeeee;
  padding: 1.5em 0;
  margin-top: 8em;
  color: #a0a0a0;
  font-size: 0.85em;

  a {
    color: inherit;
    text-decoration: none;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.7;
    }
  }

  @media (max-width: 500px) {
    flex-direction: column;
    text-align: center;
    gap: 0.3em;
  }
`

const FooterLeft = styled.div``

const FooterRight = styled.div``

const Name = styled.span`
  font-weight: 700;
`
